import React from "react";
import { Card, CardHeader, CardTitle, CardDescription } from "components/ui/card";
import { cn } from "lib/utils";

export interface AlertFilter {
  shipment_status?: string;
  tracking_issue?: string;
  arrival?: string;
  cust_id?: string;
  pod_not_received?: string;
  pending_invoice?: string;
}

export interface AlertBucket {
  label: string;
  count: number;
  filters: AlertFilter;
}

export interface AlertCard {
  id: string;
  title: string;
  type: 'bucketed' | 'single';
  style: 'positive' | 'negative' | 'neutral' | 'total';
  count?: number;
  filters?: AlertFilter;
  buckets?: AlertBucket[];
}

interface AlertCardProps {
  card: AlertCard;
  isActive: boolean;
  onCardClick: (card: AlertCard) => void;
  onBucketClick: (bucket: AlertBucket) => void;
  isBucketActive: (filters: AlertFilter) => boolean;
  /** Numbers are being refreshed: show tinted placeholders instead of stale counts */
  loading?: boolean;
}

const STYLE_CLASSES = {
  positive: {
    count: 'text-green-600',
    bucketCount: 'text-green-600',
    active: 'bg-green-100/60 ring-1 ring-inset ring-green-200',
    hover: 'hover:bg-green-50/70',
    skeleton: 'bg-green-200/70',
  },
  negative: {
    count: 'text-rose-600',
    bucketCount: 'text-rose-600',
    active: 'bg-rose-100/60 ring-1 ring-inset ring-rose-200',
    hover: 'hover:bg-rose-50/70',
    skeleton: 'bg-rose-200/70',
  },
  total: {
    count: 'text-blue-600',
    bucketCount: 'text-blue-600',
    active: 'bg-blue-100/60 ring-1 ring-inset ring-blue-200',
    hover: 'hover:bg-blue-50/70',
    skeleton: 'bg-blue-200/70',
  },
  neutral: {
    count: 'text-slate-600',
    bucketCount: 'text-slate-600',
    active: 'bg-slate-100/60 ring-1 ring-inset ring-slate-200',
    hover: 'hover:bg-slate-50/70',
    skeleton: 'bg-slate-200/70',
  },
};

export function AlertCardComponent({
  card,
  isActive,
  onCardClick,
  onBucketClick,
  isBucketActive,
  loading = false,
}: AlertCardProps) {
  const count = card.count !== undefined ? card.count : (card.buckets ? card.buckets.reduce((sum, b) => sum + b.count, 0) : 0);
  const styles = STYLE_CLASSES[card.style] ?? STYLE_CLASSES.neutral;
  const buckets = card.type === 'bucketed' ? card.buckets ?? [] : [];

  return (
    <div
      className={cn(
        'flex-1 min-w-0',
        card.type === 'single' && 'group cursor-pointer'
      )}
      onClick={card.type === 'single' ? () => onCardClick(card) : undefined}
    >
      <Card
        variant={card.style}
        className={cn(
          "transition-all duration-200 ease-in-out h-full overflow-hidden flex flex-col",
          card.type === 'single' && 'cursor-pointer hover:scale-[1.02]'
        )}
      >
        <div
          className={cn(
            "p-2.5 relative z-10",
            card.type === 'single' && 'h-full flex items-center justify-center'
          )}
        >
          <CardHeader className="flex flex-col p-0 items-center">
            <div className="flex flex-col space-y-0.5 w-full text-center">
              <CardDescription
                className={cn(
                  "text-lg font-medium leading-tight transition-colors",
                  card.type === 'single' && isActive ? 'text-blue-600' : styles.count,
                  card.type === 'single' && 'group-hover:opacity-80'
                )}
              >
                {loading ? (
                  <span className={cn("mx-auto my-1 block h-5 w-12 animate-pulse rounded", styles.skeleton)} />
                ) : (
                  count
                )}
              </CardDescription>
              <CardTitle
                className={cn(
                  "text-xs font-medium text-gray-500 tracking-wide leading-tight transition-colors",
                  card.type === 'single' && 'group-hover:text-gray-700'
                )}
              >
                {card.title}
              </CardTitle>
            </div>
          </CardHeader>
        </div>

        {buckets.length > 0 && (
          <div
            className="mt-auto bg-white/40 backdrop-blur-sm border-t border-white/20 p-1.5 grid gap-1 relative z-10"
            style={{ gridTemplateColumns: `repeat(${buckets.length}, minmax(0, 1fr))` }}
          >
            {buckets.map((bucket) => {
              const active = isBucketActive(bucket.filters);
              const empty = bucket.count === 0;

              return (
                <button
                  key={bucket.label}
                  type="button"
                  aria-pressed={active}
                  title={`${bucket.count} ${bucket.label}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onBucketClick(bucket);
                  }}
                  className={cn(
                    "min-w-0 flex flex-col items-center justify-center rounded-md px-1 py-1 text-center",
                    "transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-gray-400",
                    active ? styles.active : styles.hover
                  )}
                >
                  <span
                    className={cn(
                      "text-sm font-semibold leading-tight tabular-nums",
                      empty && !active ? 'text-gray-400' : styles.bucketCount
                    )}
                  >
                    {loading ? (
                      <span className={cn("my-0.5 block h-3.5 w-7 animate-pulse rounded", styles.skeleton)} />
                    ) : (
                      bucket.count
                    )}
                  </span>
                  <span
                    className={cn(
                      "w-full truncate text-[11px] leading-tight",
                      active ? 'text-gray-800' : empty ? 'text-gray-400' : 'text-gray-600'
                    )}
                  >
                    {bucket.label}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
